import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ARTOMEUI_ENV_GROUPS,
  ENV_GROUP_LABEL,
  useDevices,
  useDeviceGroups,
  useGgHealthBatch,
  useStuckSettingsBatch,
  useMe,
  type DeviceGroupCard,
  type DeviceSummary,
  type EnvGroup,
  type GgHealth,
  type MenderDeployment,
} from '../api/hooks';
import { DeviceHoverCard } from '../components/DeviceHoverCard';
import { MoveModal } from '../components/MoveModal';
import { ggHealthPill } from '../components/ggHealth';

export function OpsDeviceGroups() {
  const me = useMe();
  const groups = useDeviceGroups();
  // DE-161 — pull DeviceSummary rows so the hover card on each device-row
  // can show friendly name, fw + mender versions, last seen, location.
  // useDeviceGroups() returns only thing-name strings; everything else
  // lives in useDevices(). TanStack Query dedupes — Dashboard / Map etc.
  // already subscribe, so the extra hook here is free at runtime.
  const devices = useDevices();
  const deviceByThing = useMemo(() => {
    const m = new Map<string, DeviceSummary>();
    for (const d of devices.data?.devices ?? []) m.set(d.thing, d);
    return m;
  }, [devices.data]);
  // DE-279 — ONE control-plane health batch for the whole page (all cards'
  // devices), not one per card: a single POST per minute keeps the Lambda's
  // per-thing cache effective instead of fanning out to parallel containers.
  const allThings = useMemo(
    () => (groups.data?.groups ?? []).flatMap((g) => g.devices),
    [groups.data],
  );
  const ggBatch = useGgHealthBatch(allThings, me.data?.role === 'super-admin');
  // DE-287 — desired keys no device is converging on. Every one of them makes
  // AWS re-send the shadow delta on each reported update, so they cost
  // cellular data until an operator clears them.
  const stuckBatch = useStuckSettingsBatch(allThings, me.data?.role === 'super-admin');
  const stuckDevices = Object.entries(stuckBatch.data?.devices ?? {});
  const ggByThing: Record<string, GgHealth> = ggBatch.data?.devices ?? {};
  const [moveTarget, setMoveTarget] = useState<{ thing: string; current: EnvGroup } | null>(null);

  if (me.data && me.data.role !== 'super-admin') {
    return (
      <div className="card max-w-md">
        <h2 className="font-semibold mb-1">Forbidden</h2>
        <p className="text-sm text-muted">
          Device thing-group management is restricted to super-admin.
        </p>
      </div>
    );
  }

  const cardByName = new Map<EnvGroup, DeviceGroupCard>();
  for (const c of groups.data?.groups ?? []) cardByName.set(c.name, c);

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Device groups</h1>
          <p className="text-sm text-muted">
            Promote unassigned devices into dev/demo/prod, or move between environments.
            Greengrass component versions shown are what the latest deployment for that group
            will deliver.
          </p>
        </div>
        <Link to="/ops/devices" className="text-accent text-sm hover:underline">
          ← All devices
        </Link>
      </div>

      {stuckDevices.length > 0 && (
        <div className="card border border-warn/40">
          <h2 className="text-sm font-semibold text-warn mb-1">
            Settings not being applied on {stuckDevices.length}{' '}
            {stuckDevices.length === 1 ? 'device' : 'devices'}
          </h2>
          <p className="text-xs text-muted mb-2">
            A requested setting the device never reports back keeps AWS re-sending
            the same shadow delta on every state report — cellular data spent for
            as long as it sits there. Clear the key from the device&rsquo;s desired
            shadow once you know it cannot be applied.
          </p>
          <ul className="space-y-1">
            {stuckDevices.map(([thing, info]) => (
              <li key={thing} className="text-xs">
                <Link
                  to={`/devices/${encodeURIComponent(thing)}`}
                  className="text-accent hover:underline font-mono"
                >
                  {thing}
                </Link>
                <span className="text-muted font-mono">
                  {' '}
                  — {info.stuck.map((sk) => sk.key).join(', ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {groups.isLoading && <p className="text-sm text-muted">Loading…</p>}
      {groups.error && (
        <p className="text-sm text-warn">{(groups.error as Error).message}</p>
      )}

      {groups.data && (
        <div className="grid gap-4 md:grid-cols-2">
          {ARTOMEUI_ENV_GROUPS.map((name) => {
            const card = cardByName.get(name);
            return (
              <GroupCardView
                key={name}
                name={name}
                card={card}
                deviceByThing={deviceByThing}
                ggByThing={ggByThing}
                onMove={(thing) => setMoveTarget({ thing, current: name })}
              />
            );
          })}
        </div>
      )}

      {moveTarget && (
        <MoveModal
          thing={moveTarget.thing}
          currentGroup={moveTarget.current}
          onClose={() => setMoveTarget(null)}
        />
      )}
    </div>
  );
}

function GroupCardView({
  name,
  card,
  deviceByThing,
  ggByThing,
  onMove,
}: {
  name: EnvGroup;
  card: DeviceGroupCard | undefined;
  deviceByThing: Map<string, DeviceSummary>;
  ggByThing: Record<string, GgHealth>;
  onMove: (thing: string) => void;
}) {
  const navigate = useNavigate();
  const devices = card?.devices ?? [];
  const deployment = card?.deployment ?? null;
  // DE-161 — track the currently hovered row so we can render the popover
  // overlay anchored to it. Local to each GroupCardView (a hover on one
  // group shouldn't affect another); plain string state because there's
  // at most one hovered row at a time per card.
  const [hoveredThing, setHoveredThing] = useState<string | null>(null);
  // The grid is md:grid-cols-2 with demo + prod (or the last two) in the
  // right column. Default-position the hover card to the right of the
  // row; flip to the left for right-column cards so it stays inside the
  // viewport. Simple heuristic that avoids per-render bounding-box
  // measurement; see plan trade-off note. ARTOMEUI_ENV_GROUPS order is
  // [dev, demo, prod, unassigned] → cols [L, R, L, R] in row-major.
  const colIndex = ARTOMEUI_ENV_GROUPS.indexOf(name);
  const flipLeft = colIndex % 2 === 1;
  return (
    <div className="card space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="font-semibold">{ENV_GROUP_LABEL[name]}</h2>
          <p className="font-mono text-[11px] text-muted">{name}</p>
        </div>
        <span className="pill-muted">
          {devices.length} {devices.length === 1 ? 'device' : 'devices'}
        </span>
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-wide text-muted mb-1">Devices</h3>
        {devices.length === 0 ? (
          <p className="text-sm text-muted">No devices in this group.</p>
        ) : (
          <ul className="divide-y divide-border -mx-2">
            {devices.map((thing) => {
              const href = `/devices/${encodeURIComponent(thing)}`;
              const go = () => navigate(href);
              const isHovered = hoveredThing === thing;
              return (
                <li
                  key={thing}
                  onClick={go}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      go();
                    }
                  }}
                  onMouseEnter={() => setHoveredThing(thing)}
                  onMouseLeave={() => setHoveredThing((curr) => (curr === thing ? null : curr))}
                  tabIndex={0}
                  role="link"
                  aria-label={`Open ${thing}`}
                  className="group/row relative flex items-center justify-between gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors hover:bg-soft focus:outline-none focus-visible:bg-soft focus-visible:ring-1 focus-visible:ring-accent"
                >
                  <span className="font-mono text-xs text-fg truncate">{thing}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    {/* DE-279 — control-plane GG health; renders nothing while
                        loading or when the endpoint is unavailable. */}
                    {(() => {
                      const pill = ggHealthPill(ggByThing[thing]);
                      if (!pill) return null;
                      const cls = { ok: 'pill-accent', warn: 'pill-warn', muted: 'pill-muted' }[
                        pill.tone
                      ];
                      return (
                        <span className={cls} title={pill.title}>
                          {pill.label}
                        </span>
                      );
                    })()}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMove(thing);
                      }}
                      className="px-2 py-0.5 text-[11px] rounded-md border border-border text-muted hover:bg-white hover:text-fg focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
                      title="Move to another group / tenant"
                    >
                      Move
                    </button>
                    <span
                      className="text-muted text-xs opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-visible/row:opacity-100"
                      aria-hidden="true"
                    >
                      ›
                    </span>
                  </div>
                  {/* DE-161 — pointer-events-none so the cursor moving over
                      the popover doesn't fight the row's mouseenter/leave
                      and so the row's onClick still routes to detail. */}
                  {isHovered && (
                    <div
                      className={`absolute top-0 z-30 pointer-events-none ${
                        flipLeft ? 'right-full mr-2' : 'left-full ml-2'
                      }`}
                    >
                      <DeviceHoverCard thing={thing} device={deviceByThing.get(thing)} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-wide text-muted mb-1">
          Greengrass components
        </h3>
        {deployment ? (
          <>
            <p className="text-[11px] text-muted mb-1">
              <span className="font-medium">{deployment.deployment_name ?? '(unnamed deployment)'}</span>
              {' '}rev {deployment.revision_id}
            </p>
            <table className="w-full text-xs">
              <tbody>
                {deployment.components.map((c) => (
                  <tr key={c.name} className="border-b border-border last:border-0">
                    <td className="py-1 pr-2 font-mono">{c.name}</td>
                    <td className="py-1 tabular-nums text-right">{c.version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <p className="text-sm text-muted">No active Greengrass deployment for this group.</p>
        )}
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-wide text-muted mb-1">
          Active Mender updates
        </h3>
        {!card?.mender_active || card.mender_active.length === 0 ? (
          <p className="text-sm text-muted">No Mender updates in flight.</p>
        ) : (
          <ul className="space-y-2">
            {card.mender_active.map((m) => (
              <MenderRow key={m.job_id} deployment={m} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MenderRow({ deployment }: { deployment: MenderDeployment }) {
  const { artifact_name, job_id, created_at, counts } = deployment;
  // Non-zero counters pop with their tone; zeros stay neutral so the eye is
  // drawn to "what's actually happening" — in-progress + failures.
  const queuedClass = counts.queued > 0 ? 'pill-accent' : 'pill-muted';
  const inProgressClass = counts.in_progress > 0 ? 'pill-accent' : 'pill-muted';
  const succeededClass = counts.succeeded > 0 ? 'pill-accent' : 'pill-muted';
  const failedClass = counts.failed > 0 ? 'pill-warn' : 'pill-muted';
  return (
    <li className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span
          className="font-mono text-xs truncate"
          title={artifact_name ? `${artifact_name} (job ${job_id})` : job_id}
        >
          {artifact_name ?? job_id}
        </span>
        <span className="text-[11px] text-muted shrink-0" title={created_at}>
          {relativeTime(created_at)}
        </span>
      </div>
      <div className="flex flex-wrap gap-1">
        <span className={queuedClass}>Queued: {counts.queued}</span>
        <span className={inProgressClass}>In progress: {counts.in_progress}</span>
        <span className={succeededClass}>Succeeded: {counts.succeeded}</span>
        <span className={failedClass}>Failed: {counts.failed}</span>
      </div>
    </li>
  );
}

// Lightweight relative-time formatter for ISO timestamps. Avoids pulling in a
// date library for one consumer; resolution falls back to "just now" for sub-
// minute, and to absolute date if older than 30 days.
function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const sec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toISOString().slice(0, 10);
}

